import type {
  PromptTemplate,
  PromptTemplateAgencyListParams,
  PromptTemplateApiResponse,
  PromptTemplateCreatePayload,
  PromptTemplateListApiResponse,
  PromptTemplateListParams,
  PromptTemplateListResponse,
  PromptTemplateUpdatePayload,
} from '../interfaces/prompt-template.interface'
import {
  transformPromptTemplateFromApi,
  transformPromptTemplateListFromApi,
} from '../interfaces/prompt-template.interface'
import { agentsApiSlice } from './agents-api.store'

type PromptTemplateTag = { type: 'PromptTemplates'; id: string }

const API_BASE = '/api/v1/prompt-templates'

const listTag: PromptTemplateTag = { type: 'PromptTemplates', id: 'LIST' }

const buildIdTag = (id: string): PromptTemplateTag => ({ type: 'PromptTemplates', id })

const buildListTags = (r: PromptTemplateListResponse | undefined): PromptTemplateTag[] =>
  r
    ? [listTag, ...r.itemList.map(({ id }) => ({ type: 'PromptTemplates' as const, id }))]
    : [listTag]

const buildMutationTags = (id: string): PromptTemplateTag[] => [listTag, buildIdTag(id)]

const transformList = (r: PromptTemplateListApiResponse) => transformPromptTemplateListFromApi(r)
const transformItem = (r: PromptTemplateApiResponse) => transformPromptTemplateFromApi(r)

const buildParams = (p?: PromptTemplateListParams): Record<string, number> => ({
  page: p?.page ?? 1,
  page_size: p?.pageSize ?? 50,
})

const queryEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPromptTemplates: builder.query<
      PromptTemplateListResponse,
      PromptTemplateListParams | undefined
    >({
      query: (p) => ({ url: API_BASE, params: buildParams(p) }),
      transformResponse: transformList,
      providesTags: (r) => buildListTags(r),
    }),
    getPromptTemplate: builder.query<PromptTemplate, string>({
      query: (id) => `${API_BASE}/${encodeURIComponent(id)}`,
      transformResponse: transformItem,
      providesTags: (_, __, id) => [buildIdTag(id)],
    }),
    getAgencyPromptTemplates: builder.query<
      PromptTemplateListResponse,
      PromptTemplateAgencyListParams
    >({
      query: (p) => ({
        url: `/api/v1/agencies/${encodeURIComponent(p.agencySlug)}/prompt-templates`,
        params: buildParams(p),
      }),
      transformResponse: transformList,
      providesTags: (r) => buildListTags(r),
    }),
  }),
})

const mutationEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createPromptTemplate: builder.mutation<PromptTemplate, PromptTemplateCreatePayload>({
      query: (body) => ({ url: API_BASE, method: 'POST', body }),
      transformResponse: transformItem,
      invalidatesTags: [listTag],
    }),
    updatePromptTemplate: builder.mutation<
      PromptTemplate,
      { id: string; payload: PromptTemplateUpdatePayload }
    >({
      query: ({ id, payload }) => ({
        url: `${API_BASE}/${encodeURIComponent(id)}`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: transformItem,
      invalidatesTags: (_, __, { id }) => buildMutationTags(id),
    }),
    deletePromptTemplate: builder.mutation<void, string>({
      query: (id) => ({ url: `${API_BASE}/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_, __, id) => buildMutationTags(id),
    }),
  }),
})

export const {
  useGetPromptTemplatesQuery,
  useGetPromptTemplateQuery,
  useGetAgencyPromptTemplatesQuery,
} = queryEndpoints

export const {
  useCreatePromptTemplateMutation,
  useUpdatePromptTemplateMutation,
  useDeletePromptTemplateMutation,
} = mutationEndpoints
